'use client';

import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { IconPicker } from '@/components/icon-picker';
import { useSpace } from '@/contexts/space-context';
import { useProject } from '@/contexts/project-context';
import { API } from '@pilot/api';

interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CreateProjectModal({ isOpen, onClose }: CreateProjectModalProps) {
  const { space } = useSpace();
  const { refreshProjects, setSelectedProject } = useProject();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('folder');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [codeManuallyEdited, setCodeManuallyEdited] = useState(false);

  useEffect(() => {
    if (!codeManuallyEdited && name) {
      const generatedCode = name
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .slice(0, 5);
      setCode(generatedCode);
    }
  }, [name, codeManuallyEdited]);

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCodeManuallyEdited(true);
    const value = e.target.value
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 5);
    setCode(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = await API.projects.create(space.slug, {
        name,
        code,
        description,
        icon,
      });

      await refreshProjects();
      setSelectedProject(data.project);
      onClose();
      setName('');
      setCode('');
      setDescription('');
      setIcon('folder');
      setCodeManuallyEdited(false);
    } catch (err: any) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      <div className="relative bg-background border rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Create Project</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 text-sm text-red-500 bg-red-50 rounded-md">
              {error}
            </div>
          )}

          <div className="flex items-start gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Icon</label>
              <IconPicker value={icon} onChange={setIcon} />
            </div>

            <div className="flex-1 space-y-2">
              <label htmlFor="project-name" className="text-sm font-medium">
                Project Name
              </label>
              <input
                id="project-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My Project"
                required
                className="w-full px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="project-code" className="text-sm font-medium">
              Project Code
            </label>
            <input
              id="project-code"
              type="text"
              value={code}
              onChange={handleCodeChange}
              placeholder="PROJ"
              required
              maxLength={5}
              className="w-24 px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary font-mono uppercase"
            />
            <p className="text-xs text-muted-foreground">
              1-5 uppercase letters or numbers. Used for task IDs (e.g., {code || 'PROJ'}-123)
            </p>
          </div>

          <div className="space-y-2">
            <label htmlFor="project-description" className="text-sm font-medium">
              Description
              <span className="text-muted-foreground font-normal ml-1">(optional)</span>
            </label>
            <textarea
              id="project-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="A brief description..."
              rows={2}
              className="w-full px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button
              type="submit"
              disabled={loading || !name || !code}
            >
              {loading ? 'Creating...' : 'Create Project'}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
